import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useCart, fmtPrice } from "@/context/CartContext";
import { Minus, Plus, ArrowLeft, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  // Configurator state
  const [variant, setVariant] = useState(null);   // { id, label, price }
  const [freestyle, setFreestyle] = useState(false);
  const [roseColor, setRoseColor] = useState("");
  const [paperColor, setPaperColor] = useState("");
  const [ribbonText, setRibbonText] = useState("");
  const [occasion, setOccasion] = useState("");
  const [specificFlowers, setSpecificFlowers] = useState("");
  const [extra, setExtra] = useState("");
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
        setVariant(null);
        setGalleryIndex(0);
      } catch { setNotFound(true); }
    })();
  }, [id]);

  // Build the gallery (safe when product is null — returns [])
  const gallery = React.useMemo(() => {
    if (!product) return [];
    const raw = product.gallery && product.gallery.length ? [...product.gallery] : [{ src: product.image, fit: "cover" }];
    const normalized = raw.map((g) => (typeof g === "string" ? { src: g, fit: "cover" } : g));
    (product.variants || []).forEach((v) => {
      if (v.image && !normalized.some((g) => g.src === v.image)) {
        normalized.push({ src: v.image, fit: "cover" });
      }
    });
    return normalized;
  }, [product]);

  // When a configurable product has per-variant images, swap the main image to match.
  useEffect(() => {
    if (!variant || !variant.image || !gallery.length) return;
    const idx = gallery.findIndex((g) => g.src === variant.image);
    if (idx >= 0 && idx !== galleryIndex) setGalleryIndex(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, gallery]);

  // Map each gallery URL → variant label (if the image belongs to a variant)
  const imageLabelBySrc = React.useMemo(() => {
    const map = {};
    (product?.variants || []).forEach((v) => {
      if (v.image) map[v.image] = v.label;
    });
    return map;
  }, [product]);

  if (notFound) {
    return (
      <div className="lf-container py-32 text-center" data-testid="product-not-found">
        <p className="font-serif text-3xl italic" style={{ color: "var(--lf-ink-soft)" }}>We couldn't find that arrangement.</p>
        <Link to="/shop" className="lf-link mt-6 inline-block">← Back to the shop</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="lf-container py-32">
        <div className="animate-pulse mx-auto" style={{ height: 320, width: "100%", maxWidth: 480, background: "var(--lf-pink-pale)", borderRadius: 24 }} />
      </div>
    );
  }

  const activeGalleryItem = gallery[galleryIndex] || gallery[0];
  const activeImage = activeGalleryItem?.src;
  const activeFit = activeGalleryItem?.fit || "cover";
  const activeRotate = activeGalleryItem?.rotate || 0;
  const activePrice = variant ? variant.price : product.price;
  const activeImageLabel = imageLabelBySrc[activeImage];

  const handleAddToCart = () => {
    if (product.is_configurable && !variant) return toast.error("Please select a size.");
    let notes = null;
    if (product.customizable) {
      const parts = [];
      if (freestyle) parts.push("Freestyle — let the florist design.");
      if (roseColor) parts.push(`${product.subcategory === "mix" ? "Palette" : "Rose color"}: ${roseColor}`);
      if (paperColor) parts.push(`${product.subcategory === "mix" ? "Wrap" : "Paper"} color: ${paperColor}`);
      if (ribbonText) parts.push(`Ribbon message: "${ribbonText}"`);
      if (extra) parts.push(`Notes: ${extra}`);
      notes = parts.length ? parts.join(" · ") : null;
    }
    add(product, { qty, variant: variant || null, notes });
    toast.success(`${variant ? variant.label : product.name} added to your bouquet`);
  };

  return (
    <div className="lf-container py-12 md:py-20" data-testid={`product-detail-${product.id}`}>
      <Link to="/shop" className="lf-link inline-flex items-center gap-2" data-testid="back-to-shop">
        <ArrowLeft size={14} /> Back to shop
      </Link>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* IMAGE GALLERY */}
        <div className="lg:col-span-7">
          <div
            className="lf-photo-card flex items-center justify-center relative"
            style={{
              aspectRatio: activeFit === "contain" ? undefined : "1 / 1",
              background: "var(--lf-pink-pale)",
              maxHeight: activeFit === "contain" ? 720 : undefined,
            }}
          >
            <img
              src={activeImage}
              alt={product.name}
              className="rounded-xl"
              style={{
                objectFit: activeFit,
                width: activeFit === "contain" ? "auto" : "100%",
                height: activeFit === "contain" ? "auto" : "100%",
                maxWidth: "100%",
                maxHeight: activeFit === "contain" ? 700 : "100%",
                display: "block",
                transform: activeRotate ? `rotate(${activeRotate}deg) scale(1.35)` : undefined,
                transformOrigin: "center center",
              }}
            />
            {activeImageLabel && (
              <span
                data-testid="active-image-label"
                className="absolute top-4 left-4 px-4 py-2 text-xs uppercase tracking-[0.22em] font-medium"
                style={{
                  background: "rgba(255,255,255,0.95)",
                  color: "var(--lf-gold-deep)",
                  border: "1px solid var(--lf-gold)",
                  borderRadius: 999,
                  fontFamily: "Outfit, sans-serif",
                  boxShadow: "0 4px 14px -6px rgba(58, 46, 42, 0.3)",
                }}
              >
                {activeImageLabel}
              </span>
            )}
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-2.5 md:gap-3" data-testid="product-gallery-thumbs">
              {gallery.map((g, i) => {
                const label = imageLabelBySrc[g.src];
                return (
                  <button
                    key={g.src}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setGalleryIndex(i)}
                    className="overflow-hidden flex flex-col"
                    style={{
                      aspectRatio: "1 / 1",
                      borderRadius: 12,
                      padding: 4,
                      background: "var(--lf-pink-pale)",
                      border: `2px solid ${i === galleryIndex ? "var(--lf-gold)" : "transparent"}`,
                      position: "relative",
                    }}
                  >
                    <img
                      src={g.src}
                      alt=""
                      className="w-full h-full rounded-md"
                      style={{ objectFit: g.fit || "cover" }}
                    />
                    {label && (
                      <span
                        className="absolute bottom-1.5 left-1.5 right-1.5 text-[9px] uppercase tracking-wider font-medium text-center py-0.5"
                        style={{
                          background: "rgba(255,255,255,0.94)",
                          color: "var(--lf-gold-deep)",
                          borderRadius: 999,
                        }}
                      >
                        {label}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* DETAILS */}
        <div className="lg:col-span-5">
          {product.tag && (
            <span
              className="inline-block text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 mb-4"
              style={{ background: "var(--lf-mint)", color: "var(--lf-ink)", borderRadius: 999 }}
            >
              {product.tag}
            </span>
          )}
          <span className="lf-eyebrow">{product.subcategory ? `${product.category} · ${product.subcategory}` : product.category}</span>
          <h1
            className="font-serif mt-3 leading-[0.95]"
            style={{ fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)", fontWeight: 300, color: "var(--lf-ink)" }}
            data-testid="product-name"
          >
            {product.name}
          </h1>
          <p className="mt-4 text-3xl font-serif italic" style={{ color: "var(--lf-gold-deep)" }} data-testid="product-price">
            {fmtPrice(activePrice)}
          </p>
          <p className="mt-6 leading-relaxed text-base" style={{ color: "var(--lf-ink-soft)" }} data-testid="product-description">
            {product.description}
          </p>

          {/* VARIANT PICKER */}
          {product.is_configurable && product.variants && (
            <section className="mt-8" data-testid="variant-picker">
              <div className="flex items-center gap-3 mb-4">
                <span className="lf-eyebrow">1 · Pick your count</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-3 gap-2.5">
                {product.variants.map((v) => {
                  const isActive = variant && variant.id === v.id;
                  return (
                    <button
                      key={v.id}
                      data-testid={`variant-${v.id}`}
                      onClick={() => setVariant(v)}
                      className="text-left p-3 transition-all"
                      style={{
                        border: `1.5px solid ${isActive ? "var(--lf-gold)" : "var(--lf-border)"}`,
                        background: isActive ? "var(--lf-pink-pale)" : "white",
                        borderRadius: 14,
                      }}
                    >
                      <div className="text-sm font-medium" style={{ color: "var(--lf-ink)" }}>{v.label}</div>
                      <div className="text-xs mt-1" style={{ color: "var(--lf-gold-deep)", fontWeight: 500 }}>{fmtPrice(v.price)}</div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* CUSTOMIZATION */}
          {product.customizable && (
            <section className="mt-8" data-testid="customization-form">
              <span className="lf-eyebrow">{product.category === "merch" ? "Choose your options" : "2 · Personalize"}</span>

              {/* Merch option dropdowns */}
              {product.options && product.options.length > 0 && (
                <div className="mt-4 space-y-3" data-testid="product-options">
                  {product.options.map((opt) => (
                    <div key={opt.name}>
                      <span className="lf-eyebrow block mb-2" style={{ fontSize: "0.62rem" }}>{opt.name}</span>
                      <div className="flex flex-wrap gap-2">
                        {opt.values.map((val) => {
                          const active = selectedOptions[opt.name] === val;
                          return (
                            <button
                              key={val}
                              data-testid={`option-${opt.name.toLowerCase()}-${val.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                              onClick={() => setSelectedOptions((prev) => ({ ...prev, [opt.name]: val }))}
                              className="px-4 py-2.5 text-sm transition-all"
                              style={{
                                border: `1.5px solid ${active ? "var(--lf-gold)" : "var(--lf-border)"}`,
                                background: active ? "var(--lf-pink-pale)" : "white",
                                borderRadius: 999,
                                color: "var(--lf-ink)",
                                fontWeight: active ? 500 : 400,
                              }}
                            >
                              {val}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Flower-only customization */}
              {product.category !== "merch" && (
              <div className="mt-4">
                <button
                  data-testid="freestyle-toggle"
                  onClick={() => setFreestyle((f) => !f)}
                  className="w-full flex items-start gap-3 text-left p-4 transition-all"
                  style={{
                    border: `1.5px solid ${freestyle ? "var(--lf-gold)" : "var(--lf-border)"}`,
                    background: freestyle ? "var(--lf-mint)" : "white",
                    borderRadius: 14,
                  }}
                >
                  <div
                    className="flex-shrink-0 flex items-center justify-center"
                    style={{
                      width: 22, height: 22, borderRadius: 6,
                      background: freestyle ? "var(--lf-gold)" : "white",
                      border: `1.5px solid ${freestyle ? "var(--lf-gold)" : "var(--lf-border)"}`,
                    }}
                  >
                    {freestyle && <Check size={14} strokeWidth={3} color="white" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Sparkles size={15} strokeWidth={1.5} style={{ color: "var(--lf-gold-deep)" }} />
                      <span className="font-serif text-lg" style={{ color: "var(--lf-ink)" }}>
                        Freestyle · Let the florist design it
                      </span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: "var(--lf-ink-soft)" }}>
                      Surprise me — Angy and the team will design a piece around a mood.
                    </p>
                  </div>
                </button>
              </div>
              )}

              {product.category !== "merch" && !freestyle && (
                <div className="mt-4 space-y-3">
                  <label className="block">
                    <span className="lf-eyebrow" style={{ fontSize: "0.62rem" }}>
                      {product.subcategory === "mix" || product.subcategory === "vase" ? "Color palette" : "Rose color"}
                    </span>
                    <input
                      data-testid="config-rose-color"
                      className="lf-input mt-2"
                      value={roseColor}
                      onChange={(e) => setRoseColor(e.target.value)}
                      placeholder={
                        product.subcategory === "vase"
                          ? "Pastels · Warm tones · Pink & white · etc."
                          : product.subcategory === "mix"
                          ? "Pastels · Warm tones · Pink & white · etc."
                          : "Red · Pink · White · Mixed · etc."
                      }
                    />
                  </label>
                  {(product.subcategory === "vase" || product.subcategory === "mix") && (
                    <label className="block">
                      <span className="lf-eyebrow" style={{ fontSize: "0.62rem" }}>Specific flowers (optional)</span>
                      <input
                        data-testid="config-specific-flowers"
                        className="lf-input mt-2"
                        value={specificFlowers}
                        onChange={(e) => setSpecificFlowers(e.target.value)}
                        placeholder="Peonies · Hydrangea · Ranunculus · Tulips…"
                      />
                    </label>
                  )}
                  <label className="block">
                    <span className="lf-eyebrow" style={{ fontSize: "0.62rem" }}>
                      {product.subcategory === "mix" ? "Wrap color" : product.subcategory === "vase" ? "Ribbon color" : "Paper color"}
                    </span>
                    <input
                      data-testid="config-paper-color"
                      className="lf-input mt-2"
                      value={paperColor}
                      onChange={(e) => setPaperColor(e.target.value)}
                      placeholder="Black · Pink · White · Cream · Kraft · etc."
                    />
                  </label>
                  <label className="block">
                    <span className="lf-eyebrow" style={{ fontSize: "0.62rem" }}>Occasion (optional)</span>
                    <input
                      data-testid="config-occasion"
                      className="lf-input mt-2"
                      value={occasion}
                      onChange={(e) => setOccasion(e.target.value)}
                      placeholder="Birthday · Anniversary · Memorial · Just Because…"
                    />
                  </label>
                  <label className="block">
                    <span className="lf-eyebrow" style={{ fontSize: "0.62rem" }}>Ribbon message</span>
                    <input
                      data-testid="config-ribbon-text"
                      className="lf-input mt-2"
                      value={ribbonText}
                      onChange={(e) => setRibbonText(e.target.value)}
                      placeholder="Happy Birthday · Will You Be My Valentine · etc."
                      maxLength={60}
                    />
                  </label>
                </div>
              )}

              <label className="block mt-3">
                <span className="lf-eyebrow" style={{ fontSize: "0.62rem" }}>
                  {product.category === "merch" ? "Any special requests? (optional)" : "Anything else? (optional)"}
                </span>
                <textarea
                  data-testid="config-extra"
                  className="lf-input mt-2"
                  rows={2}
                  value={extra}
                  onChange={(e) => setExtra(e.target.value)}
                  placeholder={product.category === "merch" ? "Gift wrapping, personalization, etc." : "Add-ons, occasion, delivery notes…"}
                  maxLength={400}
                />
              </label>

              {product.category !== "merch" && (
                <p className="mt-4 text-xs italic" style={{ color: "var(--lf-ink-soft)" }}>
                  All add-ons & toppers are free. Doesn't include extra florals or floral 3D letters.
                </p>
              )}
            </section>
          )}

          {/* QTY + ADD TO CART */}
          <div className="mt-10 flex items-center gap-4">
            <div className="inline-flex items-center" style={{ border: "1px solid var(--lf-border)", borderRadius: 999, background: "white" }}>
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="px-3 py-3"
                aria-label="Decrease"
                data-testid="qty-decrease"
              >
                <Minus size={14} />
              </button>
              <span className="px-4 text-sm" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty((q) => Math.min(50, q + 1))}
                className="px-3 py-3"
                aria-label="Increase"
                data-testid="qty-increase"
              >
                <Plus size={14} />
              </button>
            </div>

            <button
              onClick={handleAddToCart}
              className="lf-btn lf-btn-gold flex-1 justify-center"
              data-testid="add-to-cart-button"
            >
              Add to bouquet · {fmtPrice(activePrice * qty)}
            </button>
          </div>

          <ul className="mt-10 space-y-3 text-sm" style={{ color: "var(--lf-ink-soft)" }}>
            <li className="flex justify-between border-b pb-3" style={{ borderColor: "var(--lf-border)" }}>
              <span className="lf-eyebrow">Fulfillment</span>
              <span>Delivery or pickup at checkout</span>
            </li>
            <li className="flex justify-between border-b pb-3" style={{ borderColor: "var(--lf-border)" }}>
              <span className="lf-eyebrow">Same-day</span>
              <span>Orders before 1pm</span>
            </li>
            <li className="flex justify-between">
              <span className="lf-eyebrow">Card message</span>
              <span>Add a handwritten card at checkout</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
